import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import contractsV2Service from "../services/contractsV2Service";
import essentialInstrumentsService from "../services/essentialInstrumentsService";
import { Contract } from "../types/contract";
import { safeParseFloat } from "../utils/numberUtils";

const HYDRATION_BATCH_SIZE = 100;

// Reference-data columns the API serializes as numeric strings (Prisma Decimal).
// They must be compared by their parsed numeric value, not lexicographically.
const NUMERIC_STRING_SORT_KEYS: ReadonlySet<keyof Contract> = new Set([
  "marketCap",
  "marketCapUsd",
  "volume",
  "avgDollarVolume",
  "shortableValue",
  "shortableShares",
]);

interface UseEssentialContractsParams {
  pageSize?: number;
}

export const useEssentialContracts = ({
  pageSize: initialPageSize = 20,
}: UseEssentialContractsParams = {}) => {
  // Full hydrated dataset
  const [allContracts, setAllContracts] = useState<Contract[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [missingCount, setMissingCount] = useState(0);
  const [missingIds, setMissingIds] = useState<number[]>([]);

  // Pagination state (client-side)
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);

  // Sort state (client-side)
  const [sortColumn, setSortColumn] = useState<string>("conId");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

  // Search state (client-side)
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Selection state
  const [selectedConIds, setSelectedConIds] = useState<Set<number>>(new Set());

  // Bulk action state
  const [bulkActionLoading, setBulkActionLoading] = useState(false);

  // Debounce search
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
      setCurrentPage(1);
    }, 300);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery]);

  /**
   * Fetch essential contract IDs, then hydrate in batches of 100.
   */
  const fetchContracts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setMissingCount(0);
      setMissingIds([]);
      setLoadingProgress("Fetching essential contract IDs...");

      const essentialResponse = await essentialInstrumentsService.getEssentialContracts();
      const essentialIds = essentialResponse.contracts;

      if (essentialIds.length === 0) {
        setAllContracts([]);
        setLoadingProgress("");
        return;
      }

      // Chunk into batches of HYDRATION_BATCH_SIZE
      const chunks: number[][] = [];
      for (let i = 0; i < essentialIds.length; i += HYDRATION_BATCH_SIZE) {
        chunks.push(essentialIds.slice(i, i + HYDRATION_BATCH_SIZE));
      }

      const hydrated: Contract[] = [];
      const hydratedIdSet = new Set<number>();

      // Sequential batch hydration
      for (let i = 0; i < chunks.length; i++) {
        setLoadingProgress(`Hydrating contracts... ${i + 1}/${chunks.length} batches`);
        try {
          const batchResult = await contractsV2Service.findContractsByConIds(chunks[i]);
          hydrated.push(...batchResult);
          batchResult.forEach((c) => hydratedIdSet.add(c.conId));
        } catch (batchErr) {
          console.error(`Batch ${i + 1} hydration failed:`, batchErr);
          // Continue with remaining batches
        }
      }

      // Track missing IDs (essential but not found in contracts DB)
      const missing = essentialIds.filter((id) => !hydratedIdSet.has(id));
      setMissingCount(missing.length);
      setMissingIds(missing);

      setAllContracts(hydrated);
      setLoadingProgress("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to fetch essential contracts");
      setLoadingProgress("");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchContracts();
  }, [fetchContracts]);

  // Client-side filtering
  const filteredContracts = useMemo(() => {
    if (!debouncedSearchQuery) return allContracts;

    const query = debouncedSearchQuery.toLowerCase();
    return allContracts.filter((c) => {
      return (
        String(c.conId).includes(query) ||
        c.symbol?.toLowerCase().includes(query) ||
        c.longName?.toLowerCase().includes(query) ||
        c.isin?.toLowerCase().includes(query) ||
        c.exchange?.toLowerCase().includes(query) ||
        c.primaryExchange?.toLowerCase().includes(query) ||
        c.currency?.toLowerCase().includes(query) ||
        c.secType?.toLowerCase().includes(query) ||
        c.remarks?.toLowerCase().includes(query)
      );
    });
  }, [allContracts, debouncedSearchQuery]);

  // Client-side sorting
  const sortedContracts = useMemo(() => {
    const sorted = [...filteredContracts];
    sorted.sort((a, b) => {
      const aVal = a[sortColumn as keyof Contract];
      const bVal = b[sortColumn as keyof Contract];

      if (aVal === null || aVal === undefined) return 1;
      if (bVal === null || bVal === undefined) return -1;

      let comparison = 0;
      if (NUMERIC_STRING_SORT_KEYS.has(sortColumn as keyof Contract)) {
        comparison = safeParseFloat(aVal as number | string) - safeParseFloat(bVal as number | string);
      } else if (typeof aVal === "number" && typeof bVal === "number") {
        comparison = aVal - bVal;
      } else if (typeof aVal === "boolean" && typeof bVal === "boolean") {
        comparison = aVal === bVal ? 0 : aVal ? -1 : 1;
      } else {
        comparison = String(aVal).localeCompare(String(bVal));
      }

      return sortDirection === "asc" ? comparison : -comparison;
    });
    return sorted;
  }, [filteredContracts, sortColumn, sortDirection]);

  // Client-side pagination
  const totalItems = sortedContracts.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const paginatedContracts = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return sortedContracts.slice(start, start + pageSize);
  }, [sortedContracts, currentPage, pageSize]);

  // Reset page if it exceeds total
  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(1);
    }
  }, [currentPage, totalPages]);

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
    setCurrentPage(1);
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedConIds(new Set(paginatedContracts.map((c) => c.conId)));
    } else {
      setSelectedConIds(new Set());
    }
  };

  const handleSelectOne = (conId: number, checked: boolean) => {
    setSelectedConIds((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(conId);
      } else {
        next.delete(conId);
      }
      return next;
    });
  };

  const verifyContracts = async (idsToVerify?: number[]) => {
    const targetIds = idsToVerify || Array.from(selectedConIds);
    if (targetIds.length === 0) return;

    try {
      setBulkActionLoading(true);
      await contractsV2Service.setVerifications(targetIds);
      setSelectedConIds(new Set());
      await fetchContracts();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to set verifications");
    } finally {
      setBulkActionLoading(false);
    }
  };

  const unverifyContracts = async () => {
    if (selectedConIds.size === 0) return;

    try {
      setBulkActionLoading(true);
      await contractsV2Service.clearVerifications(Array.from(selectedConIds));
      setSelectedConIds(new Set());
      await fetchContracts();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to clear verifications");
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setCurrentPage(1);
  };

  return {
    contracts: paginatedContracts,
    allContracts: sortedContracts,
    loading,
    loadingProgress,
    error,
    missingCount,
    missingIds,
    pagination: {
      currentPage,
      setCurrentPage,
      pageSize,
      setPageSize: handlePageSizeChange,
      totalItems,
      totalPages,
    },
    sorting: {
      sortColumn,
      sortDirection,
      handleSort,
    },
    search: {
      searchQuery,
      setSearchQuery,
    },
    selection: {
      selectedConIds,
      handleSelectAll,
      handleSelectOne,
      setSelectedConIds,
    },
    actions: {
      fetchContracts,
      verifyContracts,
      unverifyContracts,
      bulkActionLoading,
    },
  };
};
